import { PaymentMethod } from '../models/Organization';
import { RoomBookingChangeStatus } from '../models/RoomBookingChange';
import { PaymentStatus, RoomBooking, RoomBookingStatus } from '../models/RoomBooking';

export type UserRoomBookingGrouping =
  | { kind: 'single'; booking: RoomBooking }
  | { kind: 'series'; seriesId: string; bookings: RoomBooking[] };

function bookingStartIsPast(startIso: string, now: Date): boolean {
  const value = startIso.trim();
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() < now.getTime();
}

function hasPendingChangeReply(booking: RoomBooking): boolean {
  const proposal = booking.changeProposal;
  return (
    proposal != null &&
    proposal.active !== false &&
    proposal.status === RoomBookingChangeStatus.PENDING_REPLY
  );
}

/** Future card-payment session still waiting on manual approval. */
export function userRoomBookingGroupsAsPendingSeries(
  booking: RoomBooking,
  now: Date,
): boolean {
  if (hasPendingChangeReply(booking)) return false;
  const seriesId = booking.recurringSeriesId?.trim();
  if (!seriesId) return false;
  if (booking.paymentMethod !== PaymentMethod.ONLINE) return false;
  if (
    booking.paymentStatus !== PaymentStatus.AMOUNT_BLOCKED &&
    booking.paymentStatus !== PaymentStatus.BLOCK_REQUESTED
  ) {
    return false;
  }
  if (booking.status !== RoomBookingStatus.PENDING_CONFIRMATION) return false;
  return !bookingStartIsPast(booking.startTime, now);
}

/** Cancelling this booking releases the shared card hold for the whole series. */
export function userRoomCancelReleasesSeries(booking: {
  recurringSeriesId?: string | null;
  paymentStatus?: PaymentStatus | null;
}): boolean {
  const seriesId = booking.recurringSeriesId?.trim();
  if (!seriesId) return false;
  return booking.paymentStatus === PaymentStatus.AMOUNT_BLOCKED;
}

/**
 * Two or more future unapproved online sessions of one series become one row,
 * in first-seen order, with sessions sorted by start.
 */
export function groupUserRoomBookings(
  items: RoomBooking[],
  now: Date = new Date(),
): UserRoomBookingGrouping[] {
  if (items.length === 0) return [];
  const bySeries = new Map<string, RoomBooking[]>();
  for (const booking of items) {
    if (!userRoomBookingGroupsAsPendingSeries(booking, now)) continue;
    const seriesId = booking.recurringSeriesId!.trim();
    const list = bySeries.get(seriesId);
    if (list) list.push(booking);
    else bySeries.set(seriesId, [booking]);
  }
  Array.from(bySeries.values()).forEach((list) => {
    list.sort((a, b) => a.startTime.localeCompare(b.startTime));
  });
  const seriesIds = new Set(
    Array.from(bySeries.entries())
      .filter((entry) => entry[1].length >= 2)
      .map((entry) => entry[0]),
  );
  const emitted = new Set<string>();
  const out: UserRoomBookingGrouping[] = [];
  for (const booking of items) {
    const seriesId = booking.recurringSeriesId?.trim();
    const grouped =
      seriesId != null &&
      seriesIds.has(seriesId) &&
      userRoomBookingGroupsAsPendingSeries(booking, now);
    if (grouped) {
      if (!emitted.has(seriesId)) {
        emitted.add(seriesId);
        out.push({
          kind: 'series',
          seriesId,
          bookings: bySeries.get(seriesId)!,
        });
      }
    } else {
      out.push({ kind: 'single', booking });
    }
  }
  return out;
}
